import { ref } from 'vue'
import { jsonapiClient } from '@/api/client'
import type { components, paths } from '@/api/schema'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { usePagination } from '@/composables/usePagination'
import { type Resource } from '@/api/resources'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'

type Company = components['schemas']['CompanyResource']
type CompaniesQueryParams = paths['/api/companies']['get']['parameters']['query']
type CompanyShowInclude = NonNullable<paths['/api/companies/{id}']['get']['parameters']['query']>['include']

export function useCompanies(apiParams?: import('vue').ComputedRef<Partial<CompaniesQueryParams>>) {
  const { t } = useI18n()
  const { handleError } = useApiErrorHandler()

  const pagination = usePagination<Company, CompaniesQueryParams>(async (params) => {
    try {
      const response = await jsonapiClient.get('/api/companies', { params })
      return {
        data: response.data.data,
        meta: response.data.meta,
      }
    } catch (error) {
      handleError(error)
      return null
    }
  }, apiParams)

  const detailIncluded = ref<Resource<'contact' | 'service_location'>[]>([])

  const fetchCompany = async (id: string, include?: CompanyShowInclude) => {
    try {
      const response = await jsonapiClient.get('/api/companies/{id}', {
        path: { id },
        params: include ? { include } : undefined,
      })

      detailIncluded.value = response.data.included ?? []

      return response.data.data
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // `silent`: mirrors useContacts#create. Quick-capture's one « Créer » can mint a
  // company, its site contact and their address on the way to the job — four
  // toasts for one act. The form toasts the JOB; the rest go quiet.
  const create = async (
    data: { company_name: string; email?: string | null; phone?: string | null; website?: string | null; notes?: string | null },
    options?: { silent?: boolean },
  ) => {
    try {
      const response = await jsonapiClient.post('/api/companies', {
        data: { type: 'company', attributes: data },
      })
      pagination.prependItem(response.data.data)
      if (!options?.silent) toast.success(t('companies.createSuccess'))
      return response.data.data
    } catch (error) {
      handleError(error)
      return null
    }
  }

  const update = async (id: string, data: Partial<Company['attributes']>, opts: { silent?: boolean } = {}) => {
    try {
      const response = await jsonapiClient.patch('/api/companies/{id}', {
        data: { id, type: 'company', attributes: data },
      }, { path: { id } })
      const updated = response.data.data
            pagination.replaceItem(id, updated)
      if (!opts.silent) toast.success(t('companies.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  const remove = async (id: string) => {
    try {
      await jsonapiClient.delete('/api/companies/{id}', { path: { id } })
      pagination.removeItem(id)
      toast.success(t('companies.deleteSuccess'))
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

  return {
    companies: pagination.data,
    included: detailIncluded,
    loading: pagination.loading,
    meta: pagination.meta,
    hasMore: pagination.hasMore,
    failed: pagination.failed,
    fetchCompanies: pagination.fetchPage,
    refetchCompanies: pagination.refetch,
    loadMoreCompanies: pagination.loadMore,
    fetchCompany,
    create,
    update,
    remove,
  }
}
